import Switch from "@mui/material/Switch";
import { alpha, styled } from "@mui/material/styles";

// Function to translate user input into a standardized format
// Function to translate user input into a standardized format
const translateInput = (input) => {
  const inputStr = input.toString().toLowerCase(); // Ensure input is treated as a string
  // console.log("Input received:", inputStr); // Check the received input

  const amPmMatch = inputStr.match(/(a|am|p|pm)$/); // Check if input ends with a, am, p, or pm
  let timePart = timeToDigits(inputStr); // Remove am/pm from the time part
  const isPM = amPmMatch && (amPmMatch[0] === "p" || amPmMatch[0] === "pm");
  const isAM = amPmMatch && (amPmMatch[0] === "a" || amPmMatch[0] === "am");

  if (timePart.length === 1) {
    const hour = parseInt(timePart.substring(0, 1), 10);
  }

  // Handle inputs with 3 digits (e.g., 245, 245p -> 2:45am/pm)
  if (timePart.length === 3) {
    const hour = parseInt(timePart.substring(0, 1), 10);
    const minute = timePart.substring(1);
    const formattedTime = `${hour % 12}:${minute.padEnd(2, "0")}${
      isPM ? "pm" : "am"
    }`;
    return formattedTime;
  }

  // Handle inputs with 4 digits (e.g., 1245, 1115a -> 12:45pm/11:15am)
  if (timePart.length === 4) {
    const hour = parseInt(timePart.substring(0, 2), 10);
    const minute = timePart.substring(2);
    const formattedTime = `${hour % 12 || 12}:${minute.padEnd(2, "0")}${
      isPM ? "pm" : isAM ? "am" : ""
    }`;
    return formattedTime;
  }

  // Translate inputs like "2p" to "2:00 PM"
  if (timePart.length < 3) {
    const hour = parseInt(timePart, 10);
    return `${hour % 12 || 12}:00${isPM ? "pm" : "am"}`; // Convert to 12-hour format with AM/PM
  }

  return inputStr; // Return the input as is if no translation rule applies
};

// Function to translate a time into a digit-only string for matching (e.g., "2:45am" -> "245")
const timeToDigits = (time) => {
  return time.replace(/[^0-9]/g, ""); // Remove non-numeric characters
};

// Custom filter logic using the translateInput function
export const filterOptions = (options, { inputValue }) => {
  // console.log("Filtering options for:", inputValue); // Check the inputValue before translation

  const inputDigits = inputValue.replace(/[^0-9]/g, ""); // Extract digits from the input
  const isAM = inputValue.toLowerCase().includes("a");
  const isPM = inputValue.toLowerCase().includes("p");

  if (isAM || isPM) {
    const translatedInput = translateInput(inputValue);
    const exactOptions = options.filter((option) => {
      return option === translatedInput;
    });
    if (exactOptions.length > 0) {
      return [exactOptions[0]];
    }
  }

  //// console.log("Translated input:", translatedInput); // Check the translated input
  const filteredOptions = options.filter((option) => {
    //option.toLowerCase().includes(translatedInput.toLowerCase())
    const timeDigits = timeToDigits(option);

    // Check if AM or PM filter is applied
    if (isAM && !option.toLowerCase().includes("am")) return false;
    if (isPM && !option.toLowerCase().includes("pm")) return false;

    return timeDigits.includes(inputDigits); // Match digits
  });
  return filteredOptions;
};

function getRoundedTimeParts(value, roundToQuarter = true) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return {
      displayHour: NaN,
      minutes: NaN,
      daypart: "am",
      nextDay: "",
    };
  }

  // Scheduled shifts are authored on a quarter-hour grid, so rounding to it
  // reads as intended. Punches are not, and rounding one to 11:00a hides the
  // fact that somebody clocked in at 11:07.
  const roundedValue = roundToQuarter
    ? Math.round(numericValue * 4) / 4
    : Math.round(numericValue * 60) / 60;
  let hourValue = roundedValue % 24;
  if (hourValue < 0) {
    hourValue += 24;
  }

  const hour24 = Math.trunc(hourValue);
  const minutes = Math.round((hourValue % 1) * 60);

  return {
    displayHour: hour24 % 12 || 12,
    minutes,
    daypart: hour24 >= 12 ? "pm" : "am",
    nextDay: numericValue > 24 ? "(n)" : "",
  };
}

function formatRoundedTime(
  value,
  amSuffix,
  pmSuffix,
  includeMinutes = true,
  roundToQuarter = true
) {
  const { displayHour, minutes, daypart, nextDay } = getRoundedTimeParts(
    value,
    roundToQuarter
  );
  const minuteStr = includeMinutes
    ? `:${minutes.toString().padStart(2, "0")}`
    : "";
  const suffix = daypart === "pm" ? pmSuffix : amSuffix;

  return `${displayHour}${minuteStr}${suffix}${nextDay}`;
}

export function get_t_str(value) {
  return formatRoundedTime(value, "am", "pm");
}

export function get_t_str_shortened(value) {
  return formatRoundedTime(value, "a", "p");
}

// Same compact form as get_t_str_shortened, to the minute. For times that were
// measured rather than planned, where get_t_str_minute_precision's longer
// am/pm suffix does not fit.
export function get_t_str_shortened_minute_precision(value) {
  return formatRoundedTime(value, "a", "p", true, false);
}

export function business_decline_suffix(shift) {
  return shift?.business_decline ? " (BD)" : "";
}

export function get_t_str_shortened_max(value) {
  return formatRoundedTime(value, "a", "p", false);
}

export function get_t_str_minute_precision(value) {
  // Handle values greater than 24 (next day)
  let next_day = value > 24 ? "(n)" : "";

  // Get the hour part (0-23)
  let hour_num_temp = value % 24;

  // Handle special case for midnight (24 = 12am)
  if (value == 24) {
    return "12:00am";
  }

  // Round to nearest minute
  const total_minutes = Math.round(hour_num_temp * 60) % (24 * 60);
  const hour_num = Math.floor(total_minutes / 60);
  const minutes = total_minutes % 60;

  // Determine AM/PM
  const daypart = hour_num >= 12 ? "pm" : "am";

  // Convert from 24-hour to 12-hour format
  const display_hour =
    hour_num > 12 ? hour_num - 12 : hour_num === 0 ? 12 : hour_num;

  // Always include minutes in the format
  const timeStr = `${display_hour}:${minutes
    .toString()
    .padStart(2, "0")}${daypart}`;

  return timeStr + next_day;
}

export function getScheduleDayEndQhnum(start_qhnum, schedule_day_end_qhnum) {
  const normalizedStartQhnum = Number(start_qhnum);
  const defaultEndTime =
    24 + (Number.isFinite(normalizedStartQhnum) ? normalizedStartQhnum : 0);
  const configuredEndTime = Number(schedule_day_end_qhnum);

  if (!Number.isFinite(configuredEndTime)) {
    return defaultEndTime;
  }

  return Math.max(defaultEndTime, configuredEndTime);
}

export function generateLaborSummary(
  forecasted,
  actual,
  get_t_str,
  label1,
  label2,
  includeZeroRows = false
) {
  const laborSummary = {};
  const roleTypes = new Set();
  const supportNames = new Set();

  // Process forecasted labor
  for (const forecastedObj of forecasted) {
    const { qh_num, role_type, labor, hours, support_breakdown } =
      forecastedObj;
    const time = get_t_str(qh_num);
    roleTypes.add(role_type); // Add role_type to the set of roleTypes

    const breakdown = Array.isArray(support_breakdown) ? support_breakdown : [];
    const supportTotal = hours * 4 - labor;

    if (!laborSummary[qh_num]) {
      laborSummary[qh_num] = {
        qh_num,
        time,
        [`${label1} Service Total`]: labor,
        [`${label2} Labor Total`]: 0,
        [`${label1} Support Total`]: supportTotal,
      };
    } else {
      laborSummary[qh_num][`${label1} Service Total`] += labor;
      laborSummary[qh_num][`${label1} Support Total`] += supportTotal;
    }

    if (!laborSummary[qh_num][`${role_type} ${label1} Service`]) {
      laborSummary[qh_num][`${role_type} ${label1} Service`] = labor;
    } else {
      laborSummary[qh_num][`${role_type} ${label1} Service`] += labor;
    }

    if (!laborSummary[qh_num][`${role_type} ${label1} Support`]) {
      laborSummary[qh_num][`${role_type} ${label1} Support`] = supportTotal;
    } else {
      laborSummary[qh_num][`${role_type} ${label1} Support`] += supportTotal;
    }

    breakdown.forEach((b) => {
      supportNames.add(b.name);
      const key = `${b.name} Support`;
      if (!laborSummary[qh_num][key]) {
        laborSummary[qh_num][key] = b.labor;
      } else {
        laborSummary[qh_num][key] += b.labor;
      }
    });
  }

  // Process actual scheduled labor
  for (const actualObj of actual) {
    const { qh_num, role_type, labor } = actualObj;
    const time = get_t_str(qh_num);
    roleTypes.add(role_type); // Add role_type to the set of roleTypes

    if (!laborSummary[qh_num]) {
      laborSummary[qh_num] = {
        qh_num,
        time,
        [`${label1} Service Total`]: 0,
        [`${label2} Labor Total`]: labor,
        [`${label1} Support Total`]: 0,
      };
    } else {
      laborSummary[qh_num][`${label2} Labor Total`] += labor;
    }

    if (!laborSummary[qh_num][`${role_type} ${label2} Labor`]) {
      laborSummary[qh_num][`${role_type} ${label2} Labor`] = labor;
    } else {
      laborSummary[qh_num][`${role_type} ${label2} Labor`] += labor;
    }
  }

  // Ensure every entry has all support breakdown keys
  Object.values(laborSummary).forEach((entry) => {
    supportNames.forEach((name) => {
      const key = `${name} Support`;
      if (entry[key] === undefined) {
        entry[key] = 0;
      }
    });
  });

  // Sort the resulting array by qh_num
  const sortedSummary = Object.values(laborSummary).sort(
    (a, b) => a.qh_num - b.qh_num
  );

  if (includeZeroRows) {
    return {
      summary: sortedSummary,
      roleTypes: Array.from(roleTypes),
      supportNames: Array.from(supportNames),
    };
  }

  // Filter to only include qh_nums where totals are above 0
  const filteredSummary = sortedSummary.filter(
    ({
      [`${label2} Labor Total`]: scheduled,
      [`${label1} Service Total`]: service,
      [`${label1} Support Total`]: support,
    }) => scheduled > 0 || service > 0 || support > 0
  );

  return {
    summary: filteredSummary,
    roleTypes: Array.from(roleTypes),
    supportNames: Array.from(supportNames),
  };
}

export function getDailyScheduleHourRange(
  defaultOperatingHours,
  dayOperatingHours,
  dayShifts = [],
  fallbackStart = 0,
  fallbackEnd = 24,
  dayLaborRows = []
) {
  const hours =
    (Array.isArray(dayOperatingHours) && dayOperatingHours[0]) ||
    (Array.isArray(defaultOperatingHours) && defaultOperatingHours[0]) ||
    {};

  const parsedOpen = Number(hours.open_time);
  const parsedClose = Number(hours.close_time);
  let openTime = Number.isFinite(parsedOpen) ? parsedOpen : fallbackStart;
  let closeTime = Number.isFinite(parsedClose) ? parsedClose : fallbackEnd;

  const validShifts = (Array.isArray(dayShifts) ? dayShifts : []).filter(
    (shift) =>
      Number.isFinite(Number(shift?.shift_start)) &&
      Number.isFinite(Number(shift?.shift_end))
  );

  if (validShifts.length > 0) {
    openTime = Math.min(
      openTime,
      ...validShifts.map((shift) => Number(shift.shift_start))
    );
    closeTime = Math.max(
      closeTime,
      ...validShifts.map((shift) => Number(shift.shift_end))
    );
  }

  const laborQuarterHours = (Array.isArray(dayLaborRows) ? dayLaborRows : [])
    .filter((row) => row?.qh_num !== null && row?.qh_num !== undefined)
    .map((row) => Number(row.qh_num))
    .filter(Number.isFinite);
  if (laborQuarterHours.length > 0) {
    openTime = Math.min(openTime, ...laborQuarterHours);
    closeTime = Math.max(closeTime, ...laborQuarterHours.map((qh) => qh + 0.25));
  }

  return {
    start_h: Number.isFinite(openTime) ? Math.floor(openTime) : fallbackStart,
    end_h: Number.isFinite(closeTime) ? Math.ceil(closeTime) : fallbackEnd,
  };
}

export function fillLaborSummaryQuarterHours(
  data,
  get_t_str,
  label1,
  label2,
  start_h,
  end_h,
  supportNames = []
) {
  if (!Number.isFinite(start_h) || !Number.isFinite(end_h) || end_h <= start_h) {
    return data || [];
  }

  const rowsByQuarterHour = new Map(
    (data || [])
      .filter((item) => Number.isFinite(Number(item?.qh_num)))
      .map((item) => [Math.round(Number(item.qh_num) * 4), item])
  );

  const filledRows = [];
  const startQuarterHour = Math.round(start_h * 4);
  const endQuarterHour = Math.round(end_h * 4);

  for (
    let quarterHour = startQuarterHour;
    quarterHour <= endQuarterHour;
    quarterHour += 1
  ) {
    const qh_num = quarterHour / 4;
    const row = {
      qh_num,
      time: get_t_str(qh_num),
      [`${label1} Service Total`]: 0,
      [`${label1} Support Total`]: 0,
      [`${label2} Labor Total`]: 0,
    };

    supportNames.forEach((name) => {
      row[`${name} Support`] = 0;
    });

    const existingRow = rowsByQuarterHour.get(quarterHour);
    filledRows.push(
      existingRow
        ? {
            ...row,
            ...existingRow,
            qh_num,
            time: existingRow.time || row.time,
          }
        : row
    );
  }

  return filledRows;
}

// Function that gets the role type
export const getRoleType = (role_id, role_lookups) => {
  const role_info = role_lookups.filter((r) => r.id === role_id);
  if (role_info.length > 0) {
    return role_info[0]["role_type"];
  } else {
    return null;
  }
};

export const getRoleName = (role_id, role_lookups) => {
  const role_info = role_lookups.filter((r) => r.id === role_id);
  if (role_info.length > 0) {
    return role_info[0]["role_name"];
  } else {
    return null;
  }
};

// Sets the shift card color based on the the type of role
export function getCardColor(role_id, roleNames, roleTypes, lockedMode) {
  const roleType = getRoleType(role_id, roleNames);

  return getColorFromRoleType(roleType, roleTypes, lockedMode);
}

const getLockedColorLookup = {
  "bg-violet-600": "bg-violet-700",
  "bg-sky-700": "bg-sky-800",
  "bg-cyan-600": "bg-cyan-700",
  "bg-emerald-600": "bg-emerald-700",
  "bg-lime-600": "bg-lime-700",
  "bg-amber-500": "bg-amber-600",
  "bg-orange-600": "bg-orange-700",
  "bg-red-600": "bg-red-700",
  "bg-pink-600": "bg-pink-700",
};

export function getCardColor2(role_id, roleNames, lockedMode, shiftCard) {
  const foundRole = roleNames?.find((role) => role.id === role_id);

  if (!foundRole)
    return lockedMode
      ? "bg-slate-700"
      : `bg-slate-600 ${shiftCard ? "cursor-pointer" : ""}`;

  if (foundRole.color === null)
    return lockedMode
      ? "bg-slate-700"
      : `bg-slate-600 ${shiftCard ? "cursor-pointer" : ""}`;

  return lockedMode
    ? // Not every role colour has a darker twin in the lookup. Falling back to
      // the base colour keeps the card painted rather than leaving it with no
      // background at all.
      getLockedColorLookup[foundRole.color] || foundRole.color
    : `${foundRole.color} ${shiftCard ? "cursor-pointer" : ""}`;
}

export function getColorFromRoleType(roleType, roleTypes, lockedMode) {
  const colorList = [
    ["bg-orange-600", "bg-orange-700"],
    ["bg-sky-700", "bg-sky-800"],
  ];

  // Default colors if the roleType is not found
  let baseColor = "bg-gray-700";
  let hoverColor = "bg-gray-800";

  const roleTypesArr = roleTypes.map((role) => role.role_type); // Assuming roleNames is an array of objects with role_type attribute

  // Find the index of the roleType in the roleTypes array
  const index = roleTypesArr.indexOf(roleType);

  // If the roleType is found, use its index to get the corresponding color from colorList
  // If the colorList is not long enough, restart from the beginning using the modulo operator
  if (index !== -1) {
    const colorIndex = index % colorList.length;
    [baseColor, hoverColor] = colorList[colorIndex];
  }

  return lockedMode
    ? hoverColor
    : `${baseColor} hover:${hoverColor} cursor-pointer`;
}

export function colorLookup(tailwind_color) {
  const colors = {
    "bg-orange-700": "#ea580c",
    "bg-sky-800": "#0369a1",
    "bg-gray-800": "#4a5568",
  };
  return colors[tailwind_color];
}

export function convertBarColor(tailwind_color) {
  const colors = {
    "bg-orange-700": "#fff7ed",
    "bg-sky-800": "#eff6ff",
    "bg-gray-800": "#f9fafb",
  };
  return colors[tailwind_color];
}

export function convertBorderColor(tailwind_color) {
  const colors = {
    "bg-orange-700": "#fb923c",
    "bg-sky-800": "#60a5fa",
    "bg-gray-800": "#9ca3af",
  };
  return colors[tailwind_color];
}

export const text_to_num = (text) => {
  if (text === "12:00am") {
    return 24;
  }
  let arr = text.split(":");
  let adj = 0;
  let hours = parseInt(text);
  if (text.toLowerCase().includes("p") & (hours !== 12)) {
    adj += 12;
  }
  if (text.toLowerCase().includes("a") & (hours === 12)) {
    adj -= 12;
  }
  if (text.toLowerCase().includes("n")) {
    adj += 24;
  }
  let num = 0;
  if ((text.length === 0) | isNaN(hours)) {
    num = 0;
  } else if (arr.length > 1) {
    let minutes =
      (arr[1].length > 0) & !isNaN(parseInt(arr[1]))
        ? parseInt(arr[1]) / 60
        : 0;
    num = adj + hours + minutes;
  } else {
    num = parseInt(text) + adj;
  }
  return Math.max(Math.round(num * 4) / 4, 0);
};

export const text_to_num_minute_precision = (text) => {
  if (text === "12:00am") {
    return 24;
  }

  const arr = text.split(":");
  let adj = 0;
  const hours = parseInt(text);

  if (text.toLowerCase().includes("p") && hours !== 12) {
    adj += 12;
  }
  if (text.toLowerCase().includes("a") && hours === 12) {
    adj -= 12;
  }
  if (text.toLowerCase().includes("n")) {
    adj += 24;
  }

  const minutes =
    arr.length > 1 && (arr[1].length > 0 && !isNaN(parseInt(arr[1])))
      ? parseInt(arr[1]) / 60
      : 0;

  const num = adj + (isNaN(hours) ? 0 : hours) + minutes;
  return Math.max(Math.round(num * 60) / 60, 0);
};

export const checkIfValid = (state, break_checked) => {
  let valid = true;
  const hasSecondBreak = (state.break_length_2 || 0) > 0;
  const firstBreakStart = state.break_start;
  const firstBreakEnd = state.break_start + state.break_length;
  const secondBreakStart = state.break_start_2;
  const secondBreakEnd = (state.break_start_2 || 0) + (state.break_length_2 || 0);

  if (state.shift_end <= state.shift_start) {
    valid = false;
  }
  if (state.shift_end - state.shift_start >= 16) {
    valid = false;
  }
  if (break_checked & (state.break_start <= state.shift_start)) {
    valid = false;
  }
  if (
    break_checked &
    (state.break_start + state.break_length >= state.shift_end)
  ) {
    valid = false;
  }
  if (break_checked & (state.break_length >= 8)) {
    valid = false;
  }
  if (state.break_length < 0) {
    valid = false;
  }
  if (hasSecondBreak && !break_checked) {
    valid = false;
  }
  if (hasSecondBreak && state.break_length <= 0) {
    valid = false;
  }
  if (hasSecondBreak && secondBreakStart <= state.shift_start) {
    valid = false;
  }
  if (hasSecondBreak && secondBreakEnd >= state.shift_end) {
    valid = false;
  }
  if (hasSecondBreak && state.break_length_2 >= 8) {
    valid = false;
  }
  if ((state.break_length_2 || 0) < 0) {
    valid = false;
  }
  if (
    break_checked &&
    hasSecondBreak &&
    secondBreakStart < firstBreakEnd &&
    secondBreakEnd > firstBreakStart
  ) {
    valid = false;
  }
  if (break_checked && hasSecondBreak && secondBreakStart < firstBreakEnd) {
    valid = false;
  }
  return valid;
};

export function createDayArray(startWeekIndex) {
  const allDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const rotatedDays = [];

  for (let i = 0; i < allDays.length; i++) {
    rotatedDays.push(allDays[(i + startWeekIndex) % allDays.length]);
  }

  return rotatedDays;
}

export const formatPersonName = ({ first_name = "", last_name = "" } = {}) => {
  const first = String(first_name).trim();

  // normalise last_name
  const last = String(last_name).trim();
  const isMissing =
    !last || ["undefined", "null", "none"].includes(last.toLowerCase());

  return isMissing ? first : `${first} ${last[0].toUpperCase()}.`;
};

export const normalizePaidBreaks = (paidBreaks) => {
  if (!paidBreaks) {
    return [];
  }
  if (Array.isArray(paidBreaks)) {
    return paidBreaks;
  }
  if (typeof paidBreaks === "object") {
    return Object.values(paidBreaks);
  }
  return [];
};

// Rest breaks can start between quarter hours; preserve their scheduled minute.
export const formatPaidBreakStarts = (paidBreaks) =>
  normalizePaidBreaks(paidBreaks)
    .filter(
      (entry) =>
        Number.isFinite(entry?.break_start) &&
        (entry.break_duration || entry.break_length || 0) > 0
    )
    .sort((a, b) => a.break_start - b.break_start)
    .map((entry) => get_t_str_shortened_minute_precision(entry.break_start))
    .join(", ");

export const normalizeBreakEntries = (breakEntries) => {
  if (!breakEntries) {
    return [];
  }
  if (Array.isArray(breakEntries)) {
    return breakEntries;
  }
  if (typeof breakEntries === "object") {
    return Object.values(breakEntries);
  }
  return [];
};

export function process_schedule_data(
  schedule,
  store_team_member_weekly_info,
  role_names,
  schedule_day_end_qhnum
) {
  const scheduleDayEndQhnum = getScheduleDayEndQhnum(
    0,
    schedule_day_end_qhnum
  );
  const schedule_data = schedule
    .filter((s, i) => s.other_store != true)
    .map((x) => {
      const color_obj = role_names.find((v, i) => v.id === x.role_id);
      x.color = color_obj ? color_obj.color : "";
      x.paid_breaks = normalizePaidBreaks(x.paid_breaks);
      x.pre_shift = x.shift_start - 0;
      if (x.break_length > 0) {
        x.pre_break = x.break_start - x.shift_start;
        x.break = x.break_length;
        x.post_break = x.shift_end - (x.break_start + x.break_length);
      } else {
        x.pre_break = x.shift_length;
        x.break = 0;
        x.post_break = 0;
      }
      x.post_shift = Math.max(0, scheduleDayEndQhnum - x.shift_end);
      const person_info = store_team_member_weekly_info.filter(
        (p) => p.id === x.store_team_member
      );
      if (person_info.length > 0) {
        x.name = formatPersonName(person_info[0]);
      } else {
        x.name = "";
      }

      return x;
    });

  return schedule_data.sort(
    (a, b) => a.shift_start > b.shift_start || a.shift_length > b.shift_length
  );
}

export function process_used_schedule_data(schedule) {
  const schedule_data = schedule
    .filter((s, i) => s.other_store != true)
    .map((x) => {
      x.name = x.store_team_member
        ? formatPersonName(x.store_team_member)
        : "No name found";
      x.stm_id = x.store_team_member?.id;
      x.paid_breaks = normalizePaidBreaks(x.paid_breaks);
      x.unpaid_breaks = normalizeBreakEntries(x.unpaid_breaks);

      const startNum = Number(x.shift_start);
      const endNum = Number(x.shift_end);
      const hasStart =
        x.shift_start !== null &&
        x.shift_start !== undefined &&
        Number.isFinite(startNum);
      const hasEnd =
        x.shift_end !== null &&
        x.shift_end !== undefined &&
        Number.isFinite(endNum);
      if (hasStart) {
        x.shift_start = startNum;
      }
      if (hasEnd) {
        x.shift_end = endNum;
      }
      if (hasStart && hasEnd && endNum < startNum) {
        x.shift_end = endNum + 24;
      }

      // Also normalize published shift's paid_breaks if present
      if (x.published) {
        x.published.paid_breaks = normalizePaidBreaks(x.published.paid_breaks);
      }

      return x;
    });

  return schedule_data.sort(
    (a, b) => a.shift_start > b.shift_start || a.shift_length > b.shift_length
  );
}

export function generateTimeOptionsDF(start_qhnum, schedule_day_end_qhnum) {
  const options = [];
  const startTime = 0;
  const endTime = getScheduleDayEndQhnum(
    start_qhnum,
    schedule_day_end_qhnum
  );

  let currentTime = startTime;

  while (currentTime <= endTime) {
    options.push({ value: currentTime, label: get_t_str(currentTime) });
    currentTime += 0.25;
  }

  return options;
}

export function generateBufferOptionsDF() {
  const options = [];
  const startTime = -3;
  const endTime = 3;

  let currentTime = startTime;

  while (currentTime <= endTime) {
    options.push({ value: currentTime, label: `${currentTime * 60} mins` });
    currentTime += 0.25;
  }

  return options;
}

export function generateTimeOptions(start_qhnum, schedule_day_end_qhnum) {
  const options = [];
  const startTime = 0;
  const endTime = getScheduleDayEndQhnum(
    start_qhnum,
    schedule_day_end_qhnum
  );

  let currentTime = startTime;

  while (currentTime <= endTime) {
    options.push(get_t_str(currentTime));
    currentTime += 0.25;
  }

  return options;
}

export function generateTimeOptionsMinutePrecision(
  start_qhnum,
  intervalMinutes = 10,
  schedule_day_end_qhnum
) {
  const options = [];
  const startTime = 0;
  const endTime = getScheduleDayEndQhnum(
    start_qhnum,
    schedule_day_end_qhnum
  );

  const intervalHours = intervalMinutes / 60;
  let currentTime = startTime;

  while (currentTime <= endTime) {
    options.push(get_t_str_minute_precision(currentTime));
    currentTime = Math.round((currentTime + intervalHours) * 600) / 600;
  }

  return options;
}

export function getColorFromGrade(grade) {
  if (grade === "A") {
    return "#059669";
  } else if (grade === "B") {
    return "#65a30d";
  } else if (grade === "C") {
    return "#f5be27";
  } else if (grade === "D") {
    return "#ea580c";
  } else if (grade === "F") {
    return "#dc2626";
  } else {
    return "gray";
  }
}

export const PurpleSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#6d28d9",
    "&:hover": {
      backgroundColor: alpha("#6d28d9", theme.palette.action.hoverOpacity),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#6d28d9",
  },
}));

export function createWarningsTooltip(numScheduleWarnings, countConflicts) {
  let tooltipText = "";
  if (numScheduleWarnings > 0) {
    tooltipText += `${numScheduleWarnings} ${
      numScheduleWarnings === 1 ? "person is" : "people are"
    } in overtime`;
  }
  if (countConflicts > 0) {
    if (numScheduleWarnings > 0) {
      tooltipText += ", ";
    }
    tooltipText += `${countConflicts} ${
      countConflicts === 1 ? "person has" : "shifts with"
    } an availability conflict`;
  }
  return tooltipText;
}

export function formatName(inputName) {
  if (!inputName) {
    return "";
  }
  // Split the input name into parts
  const nameParts = inputName.split(" ");

  // Capitalize the first letter of each part and convert the rest to lowercase
  const formattedNameParts = nameParts.map(
    (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );

  // Join the formatted parts back together
  const formattedName = formattedNameParts.join(" ");

  return formattedName;
}

export function formatTimestamp(isoString) {
  const date = new Date(isoString);

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const minutesStr = minutes < 10 ? "0" + minutes : minutes;
  return hours + ":" + minutesStr + ampm;
}

export function formatTimestampWithDate(isoString) {
  const date = new Date(isoString);

  // Format the date as MM/DD/YYYY or use your preferred format
  const day = date.getDate();
  const month = date.getMonth() + 1; // Months are zero-based
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const minutesStr = minutes < 10 ? "0" + minutes : minutes;

  // Combine the date and time
  return `${hours}:${minutesStr}${ampm} ${month}/${day}/${year}`;
}

export function getHoursAndBreaks(startTime, endTime, breaksArray, hoursRoundedToTenths) {
  // Convert both to milliseconds
  var startTimeMilliseconds = new Date(startTime).getTime();
  var endTimeMilliseconds = new Date(endTime).getTime();

  let sumBreaksMilliseconds = 0;

  breaksArray?.forEach((breakItem) => {
    var startBreakMilliseconds = new Date(breakItem.clocked_in_at).getTime();
    var endBreakMilliseconds = breakItem.clocked_out_at
      ? new Date(breakItem.clocked_out_at).getTime()
      : breakItem.clocked_in_at
      ? new Date().getTime()
      : 0;
    var breakLengthMilliseconds = Math.abs(
      endBreakMilliseconds - startBreakMilliseconds
    );
    sumBreaksMilliseconds += breakLengthMilliseconds;
  });

  var totalTimeWorkedMilliseconds = Math.abs(
    endTimeMilliseconds - startTimeMilliseconds
  );
  var paidTimeWorkedMilliseconds =
    totalTimeWorkedMilliseconds - sumBreaksMilliseconds;

  // Convert milliseconds to hours with decimal precision
  var totalPaidHours = paidTimeWorkedMilliseconds / (1000 * 60 * 60);

  // Round based on hoursRoundedToTenths parameter
  var paidHours;
  if (hoursRoundedToTenths) {
    // Round to nearest 10th (0.1)
    paidHours = Math.round(totalPaidHours * 10) / 10;
  } else {
    // Round to nearest 100th (0.01)
    paidHours = Math.round(totalPaidHours * 100) / 100;
  }

  // Keep paidMinutes for backwards compatibility
  // Calculate remaining milliseconds after subtracting full hours
  var remainingMilliseconds = paidTimeWorkedMilliseconds % (1000 * 60 * 60);
  var paidMinutes = Math.ceil(remainingMilliseconds / (1000 * 60));

  // Calculate break minutes
  var breakMinutes = Math.floor(sumBreaksMilliseconds / (1000 * 60));

  return {
    paidHours: paidHours,
    paidMinutes: paidMinutes,
    breakMinutes: breakMinutes,
  };
}

export function hoursToMinutes(hours) {
  return Math.round(hours * 60);
}

export const getAvailabilityBgColor = (availability) => {
  if (!availability) return "bg-slate-100";
  if (availability === "All Day") return "bg-green-100";
  if (availability === "Unavailable") return "bg-red-100";
  if (availability === "No Availability Set") return "bg-slate-100";
  return "bg-amber-100";
};

export const getAvailabilityBorderColor = (availability) => {
  if (!availability) return "border-slate-200";
  if (availability === "All Day") return "border-green-200";
  if (availability === "Unavailable") return "border-red-200";
  if (availability === "No Availability Set") return "border-slate-200";
  return "border-amber-200";
};

export const getAvailabilityTextColor = (availability) => {
  if (!availability) return "text-slate-700";
  if (availability === "All Day") return "text-green-800";
  if (availability === "Unavailable") return "text-red-800";
  if (availability === "No Availability Set") return "text-slate-700";
  return "text-amber-800";
};

export const getAvailabilityIconColor = (availability) => {
  if (!availability) return "#334155"; // slate-700
  if (availability === "All Day") return "#166534"; // green-800
  if (availability === "Unavailable") return "#991b1b"; // red-800
  if (availability === "No Availability Set") return "#334155"; // slate-700
  return "#92400e"; // amber-800
};

export function formatPhoneNumber(phoneNumber) {
  // Remove all non-digit characters
  const digits = String(phoneNumber || "").replace(/\D/g, "");

  if (digits.length === 12 && digits.startsWith("52")) {
    // Format Mexico E.164 as +52 XX XXXX XXXX
    return `+52 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  } else if (digits.length === 10) {
    // Format as (XXX) XXX-XXXX
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === "1") {
    // Format as +1 (XXX) XXX-XXXX
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
      7
    )}`;
  } else {
    // Return original input if it doesn't match expected patterns
    return phoneNumber;
  }
}

export function normalizeSMSPhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;
  const raw = String(phoneNumber).trim();
  const digits = raw.replace(/\D/g, "");

  if (raw.startsWith("+")) {
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (digits.length === 12 && digits.startsWith("52")) return `+${digits}`;
    return null;
  }

  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === "1") return `+${digits}`;
  return null;
}

// Mirrors backend SMS normalization: accept historical US formats, plus
// Mexico numbers when they include the +52 country code.
export function isValidSMSPhone(phoneNumber) {
  return Boolean(normalizeSMSPhoneNumber(phoneNumber));
}

export function isValidUSPhone(phoneNumber) {
  if (!phoneNumber) return false;
  const digits = String(phoneNumber).replace(/\D/g, "");
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits[0] === "1") return true;
  return false;
}

// Whether team members at this store carry a clock PIN worth showing and
// editing: the time clock uses one, and so does the task kiosk when the store
// requires a PIN there (kiosk_task_pin_required) even without the time clock.
export function clockPinEnabledForStore(storeNames, storeId) {
  const store = (storeNames || []).find((s) => s.id === storeId);
  return !!(store?.time_entries_enabled || store?.kiosk_task_pin_required);
}
